import { apiClient } from './client';
import type { ApiResponse } from '@/types/api.types';

/**
 * Journal import — stage a spreadsheet of journals, validate it, post it (GAP-017).
 *
 * The upload endpoint takes PARSED ROWS, not a file. That is the backend's contract and it is the
 * right one: the spreadsheet is a transport format, and the rules that matter — does the account
 * exist, is it a posting account, does each journal balance — are checked server-side against the
 * company's own chart of accounts by `validate`. The browser turns .xlsx into rows; the server
 * decides what any of it means.
 */

/** One row as parsed from the spreadsheet, before staging. */
export interface ImportRowInput {
  rowNumber:      number;
  /** Rows sharing a group become ONE journal. This is what makes a flat sheet into journals. */
  journalGroup:   string;
  accountCode:    string | null;
  costCenterCode: string | null;
  locationCode:   string | null;
  documentDate:   string | null;   // yyyy-MM-dd
  postingDate:    string | null;   // yyyy-MM-dd
  debitAmount:    number;
  creditAmount:   number;
  currencyCode:   string | null;
  narration:      string | null;
  reference:      string | null;
}

/** A staged row and the server's verdict on it. */
export interface ImportRow {
  id:                string;
  rowNumber:         number;
  journalGroup:      string;
  accountCode:       string | null;
  locationCode:      string | null;
  debitAmount:       number;
  creditAmount:      number;
  narration:         string | null;
  /** PENDING before validation, then VALID or INVALID. */
  validationStatus:  'PENDING' | 'VALID' | 'INVALID';
  validationMessage: string | null;
  postedJournalId:   string | null;
  wasPosted:         boolean;
}

export interface ImportBatch {
  id:                     string;
  fileName:               string | null;
  /** DRAFT → VALIDATED → SUBMITTED | POSTED, or CANCELLED. */
  status:                 string;
  rowCount:               number;
  validRowCount:          number;
  journalCount:           number;
  /** Journals that reached the ledger. */
  postedJournalCount:     number;
  /** Journals created and submitted, waiting on an approver. Not the same as posted. */
  submittedJournalCount:  number;
  rows:                   ImportRow[];
}

export const journalImportApi = {
  /** POST /api/v1/journal-import — stage the rows. Nothing posts yet. */
  upload: (fileName: string | null, rows: ImportRowInput[], remarks?: string | null) =>
    apiClient.post<ApiResponse<{ id: string }>>('/journal-import', { fileName, remarks: remarks ?? null, rows }),

  /** GET /api/v1/journal-import/{id} */
  getById: (id: string) =>
    apiClient.get<ApiResponse<ImportBatch>>(`/journal-import/${id}`),

  /** POST /api/v1/journal-import/{id}/validate — resolve accounts, check each group balances. */
  validate: (id: string) =>
    apiClient.post<ApiResponse<ImportBatch>>(`/journal-import/${id}/validate`),

  /** POST /api/v1/journal-import/{id}/post — each valid group through the journal engine. */
  post: (id: string) =>
    apiClient.post<ApiResponse<ImportBatch>>(`/journal-import/${id}/post`),

  /** POST /api/v1/journal-import/{id}/cancel — discard a staged batch. No accounting effect. */
  cancel: (id: string) =>
    apiClient.post<ApiResponse<object>>(`/journal-import/${id}/cancel`),
};
